'use client';
import { locales, type Locale } from '@bells/i18n/runtime';
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  Sidebar as SidebarRoot,
  useSidebar,
} from '@bells/ui/components/sidebar';
import { Logo } from '@bells/ui/icons/logo';
import type { NavigationItemTransform } from '@bells/ui/utils/navigation';
import * as React from 'react';
import { BrandSwitcher } from './brand-switcher';
import { NavUser } from '@/components/sidebar/nav-user';
import { useAppSettings } from '@bells/core';
import { useLanguage } from '@bells/core/i18n';
import { cn } from '@bells/core/lib/utils';
import { layoutSearchModalTitle } from '@bells/i18n';
import languages from '@bells/iso/languages';
import { Button } from '@bells/ui/components/button';
import { FlagIcon } from '@bells/ui/components/flag-icon';
import { IconBrandSignal } from '@bells/ui/icons/signal';
import { Telegram } from '@bells/ui/icons/telegram';
import { Mail, Search } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { NavMain } from './nav-main';
const data = {
  brands: [
    {
      name: 'BB Escort',
      logo: Logo,
      description: 'Find your escort',
      logoClassName: 'text-brand',
    },
  ],
};

export const Sidebar = ({
  className,
  children,
  navigation,
  locale,
  ...props
}: React.PropsWithChildren<
  Omit<React.ComponentProps<typeof SidebarRoot>, 'side'>
> & { navigation: NavigationItemTransform[]; locale: Locale }) => {
  const { setOpenMobile } = useSidebar();
  const pathname = usePathname();

  useEffect(() => {
    setOpenMobile(false);
  }, [pathname]);

  const { language } = useLanguage();

  return (
    <>
      <SidebarRoot
        className={cn('bg-sidebar', className)}
        collapsible={'offcanvas'}
        side={'right'}
        variant='sidebar'
        {...props}
      >
        <SidebarContent>
          <NavMain items={navigation} locale={locale} />
        </SidebarContent>

        <SidebarFooter className='border-r'>
          <ul className='flex w-full list-none items-center justify-between'>
            {locales
              .filter((df) => df != language)
              .map((lang) => (
                <SidebarMenuItem key={lang}>
                  <SidebarMenuButton asChild>
                    <Link
                      prefetch={false}
                      href={lang == 'de' ? '/' : `/${lang}`}
                    >
                      {lang == 'de' && (
                        <FlagIcon
                          alt={languages[lang]?.names[lang]}
                          countryCode={'DE'}
                          className='h-[1.2rem] w-[1.2rem]'
                        />
                      )}
                      {lang == 'en' && (
                        <FlagIcon
                          alt={languages[lang]?.names[lang]}
                          countryCode={'GB'}
                          className='h-[1.2rem] w-[1.2rem]'
                        />
                      )}
                      {languages[lang]?.names[lang]}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            <div className='flex items-center gap-2 px-2'>
              <Button asChild variant={'telegram'} size={'iconXs'}>
                <Link
                  rel='noopener'
                  href={'https://t.me/bbescort'}
                  target='_blank'
                >
                  <Telegram></Telegram>
                  <span className='sr-only'>Join us on Telegram</span>
                </Link>
              </Button>

              <Button asChild variant={'signal'} size={'iconXs'}>
                <Link
                  href={
                    'https://signal.me/#eu/JAGRgW9JF-FyutDBi50L5eReo8lsf15z5-ojQxvZocq58H4j8UdDzIH9CNXKwHHk'
                  }
                  rel='noopener'
                  target='_blank'
                >
                  <IconBrandSignal></IconBrandSignal>
                  <span className='sr-only'>Join us on Signal</span>
                </Link>
              </Button>
              <Button asChild variant={'mail'} size={'iconXs'}>
                <Link
                  rel='noopener'
                  href={'mailto:info@skypleasureltd.com'}
                  target='_blank'
                >
                  <Mail></Mail>
                  <span className='sr-only'>Send us an e-mail</span>
                </Link>
              </Button>
            </div>
          </ul>
        </SidebarFooter>
      </SidebarRoot>
      <SidebarInset className='w-full max-w-full'>{children}</SidebarInset>
    </>
  );
};
